//@ts-nocheck
import React from "react";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { KeyboardArrowDown } from "@mui/icons-material";
import {
  fetchRegionData,
  fetchFrontlineData,
  fetchCountryData,
  fetchBusinessTypes,
} from "../../services/TrainingsHeaderViewService";
import { useGetToken } from "../../hooks/useGetToken";
import { useGlobalStateContext } from "../../context/globalState";
import TextLabel from "../TextLabel";
import { useTranslation } from "react-i18next";

type BreadcrumbNavigationProps = {
  resetCompanyList: () => void;
  adminRole?: string;
};

const BreadcrumbNavigation: React.FC = ({
  resetCompanyList,
  adminRole,
}: BreadcrumbNavigationProps) => {
  // Auth0 subcontractor admin. The server fences them to their own vendor and
  // forces every user they create to be a subcontractor, so "Kone Internal" is
  // not a choice they have.
  const isSubconAdmin = adminRole === "SUBCON_ADMIN";
  const [getToken] = useGetToken();
  const { t } = useTranslation();
  // Reading from the context
  const {
    countryList,
    setCountryList,
    frontline,
    setFrontline,
    setCountry,
    country,
    frontlineId,
    setFrontlineId,
    regionId,
    setRegionId,
    supplierType,
    setSupplierType,
    setFormattedCountryCodes,
    beginLoading,
    endLoading,
    userPreference,
    selectedBusinessType,
    setSelectedBusinessType,
    selectedBusinessTypeId,
    setSelectedBusinessTypeId,
    businessTypesList,
    setBusinessTypesList,
    setTrainingHeaders,
    setBreadcrumbs,
    setUserPreference,
  } = useGlobalStateContext();

  // State to manage the dropdown menu
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuType, setMenuType] = React.useState<
    | "region"
    | "subRegion"
    | "country"
    | "supplier"
    | "vendor"
    | "businessType"
    | null
  >(null);

  // Business Type anchor element (separate menu)
  const [businessTypeAnchorEl, setBusinessTypeAnchorEl] =
    React.useState<null | HTMLElement>(null);

  const [regions, setRegions] = React.useState<any[]>([]);
  const [countries, setCountries] = React.useState<any[]>([]);
  const [suppliers, setSuppliers] = React.useState<any[]>([]);
  const [vendors, setVendors] = React.useState<any[]>([]);

  const [selectedRegionId, setSelectedRegionId] = React.useState<number | null>(
    null,
  );
  const [selectedFrontlineId, setSelectedFrontlineId] =
    React.useState<string>("");
  const [selectedCountryCode, setSelectedCountryCode] = React.useState<
    string[]
  >([]);
  const [selectedSupplier, setSelectedSupplier] = React.useState<string>("");

  // States for displaying names
  const [selectedRegionName, setSelectedRegionName] = React.useState<string>();
  const [selectedFrontlineName, setSelectedFronlineName] =
    React.useState<string>();
  const [selectedCountryName, setSelectedCountryName] =
    React.useState<string>("");

  // Fetch business types on mount
  React.useEffect(() => {
    const fetchBusinessTypesData = async () => {
      try {
        const token = await getToken();
        const businessTypesData = await fetchBusinessTypes(
          token,
          userPreference.languagePreference,
        );
        setBusinessTypesList(businessTypesData);
      } catch (error) {
        console.error("Error fetching business types:", error);
      }
    };

    fetchBusinessTypesData();
  }, [userPreference.languagePreference]);

  React.useEffect(() => {
    // Only fetch regions if business type is selected
    if (selectedBusinessType && regions && regions.length === 0) {
      fetchRegions();
    }
  }, [userPreference.languagePreference, selectedBusinessType, regions]);

  React.useEffect(() => {
    if (selectedRegionId) {
      const fetchSubRegionsForRegion = async () => {
        const loadingToken = beginLoading("breadcrumb-region-frontlines");
        try {
          const token = await getToken();
          const frontlineData = await fetchFrontlineData(
            token,
            selectedRegionId,
            userPreference.languagePreference,
          );
          setFrontline(frontlineData);
        } catch (error) {
          console.error("Error fetching sub-region data:", error);
        } finally {
          endLoading(loadingToken);
        }
      };

      fetchSubRegionsForRegion();
    }
  }, [selectedRegionId, regionId, userPreference.languagePreference]);

  React.useEffect(() => {
    if (selectedFrontlineId) {
      const fetchCountryDataForSubRegion = async () => {
        const loadingToken = beginLoading("breadcrumb-frontline-countries");
        try {
          const token = await getToken();
          const countryData = await fetchCountryData(
            token,
            selectedFrontlineId,
            userPreference.languagePreference,
          );
          setCountryList(countryData);
        } catch (error) {
          console.error("Error fetching country data:", error);
        } finally {
          endLoading(loadingToken);
        }
      };

      fetchCountryDataForSubRegion();
    }
  }, [selectedFrontlineId, frontline, userPreference.languagePreference]);

  React.useEffect(() => {
    // Update the selected names when the language changes
    console.log(
      "Language preference changed:",
      userPreference.languagePreference,
    );
    console.log("Current selections:", {
      selectedRegionId,
      selectedFrontlineId,
      selectedCountryCode,
      supplierType,
    });
    if (!selectedRegionId) {
      setSelectedRegionName(t("adminDashboard.dropdownSelection.region"));
      console.log("Region name set to default");
    } else if (selectedRegionName) {
      const region = regions.find((r) => r.region_id === selectedRegionId);
      console.log("Found region:", region);
      if (region) {
        setSelectedRegionName(region.region_code);
        console.log("Region name updated to:", region.region_code);
      }
    }
    if (!selectedFrontlineId) {
      setSelectedFronlineName(t("adminDashboard.dropdownSelection.frontline"));
      console.log("Frontline name set to default");
    } else if (selectedFrontlineName) {
      const frontlineItem = frontline.find(
        (f) => f.frontline_id === selectedFrontlineId,
      );
      console.log("Found frontline:", frontlineItem);
      if (frontlineItem) {
        setSelectedFronlineName(frontlineItem.frontline_code);
        console.log("Frontline name updated to:", frontlineItem.frontline_code);
      }
    }
    if (!selectedCountryCode.length) {
      setSelectedCountryName(t("adminDashboard.dropdownSelection.country"));
      console.log("Country name set to default");
    } else if (selectedCountryName) {
      const selectedNames = countryList
        .filter((c) => selectedCountryCode.includes(c.country_code))
        .map((c) => c.name);
      console.log("Selected country names:", selectedNames);
      setSelectedCountryName(selectedNames.join(", "));
    }
    if (!supplierType) {
      setSelectedSupplier(t("adminDashboard.dropdownSelection.supplier"));
      console.log("Supplier name set to default");
    } else if (supplierType === "supplier") {
      setSelectedSupplier(
        t("adminDashboard.dropdownSelection.supplierTypes.koneInternal"),
      );
      console.log(
        "Supplier name updated to:",
        t("adminDashboard.dropdownSelection.supplierTypes.koneInternal"),
      );
    } else if (supplierType === "vendor") {
      setSelectedSupplier(
        t("adminDashboard.dropdownSelection.supplierTypes.subcontractor"),
      );
      console.log(
        "Supplier name updated to:",
        t("adminDashboard.dropdownSelection.supplierTypes.subcontractor"),
      );
    }
    console.log("Final selected names:", {
      selectedRegionName,
      selectedFrontlineName,
      selectedCountryName,
      selectedSupplier,
    });
  }, [userPreference.languagePreference]);

  React.useEffect(() => {
    if (!selectedFrontlineId) {
      setSelectedFronlineName(t("adminDashboard.dropdownSelection.frontline"));
    }
  }, [
    t("adminDashboard.dropdownSelection.frontline"),
    userPreference.languagePreference,
  ]);

  // Preselect the breadcrumb dropdowns from the saved user preference
  // (GET /userSetting). Each field is applied only once, and the existing
  // cascade effects above load the dependent dropdown options:
  // businessType -> regions -> frontlines -> countries.
  const prefBusinessAppliedRef = React.useRef(false);
  const prefRegionAppliedRef = React.useRef(false);
  const prefFrontlineAppliedRef = React.useRef(false);
  const prefCountryAppliedRef = React.useRef(false);
  const prefSupplierAppliedRef = React.useRef(false);

  // 1. Business type -> triggers regions fetch
  React.useEffect(() => {
    if (prefBusinessAppliedRef.current) return;
    if (!userPreference?.businessType) return;
    if (!businessTypesList || businessTypesList.length === 0) return;
    const businessTypeItem = businessTypesList.find(
      (bt) => bt.businessTypeId === userPreference.businessType,
    );
    if (!businessTypeItem) return;
    prefBusinessAppliedRef.current = true;
    setSelectedBusinessType(businessTypeItem.businessTypeName);
    setSelectedBusinessTypeId(businessTypeItem.businessTypeId);
  }, [businessTypesList, userPreference?.businessType]);

  // 2. Region (area) -> triggers frontlines fetch
  React.useEffect(() => {
    if (prefRegionAppliedRef.current) return;
    if (!userPreference?.area) return;
    if (!regions || regions.length === 0) return;
    const regionItem = regions.find(
      (r) => r.region_id === userPreference.area,
    );
    if (!regionItem) return;
    // A saved region can become inactive later; the menu disables it, so it must
    // not be programmatically restored either.
    if (isOptionDisabled(regionItem.status)) return;
    prefRegionAppliedRef.current = true;
    setSelectedRegionId(regionItem.region_id);
    setRegionId(regionItem.region_id);
    setSelectedRegionName(regionItem.region_code);
  }, [regions, userPreference?.area]);

  // 3. Frontline -> triggers countries fetch
  React.useEffect(() => {
    if (prefFrontlineAppliedRef.current) return;
    if (!userPreference?.frontline) return;
    if (!frontline || frontline.length === 0) return;
    const frontlineItem = frontline.find(
      (f) => f.frontline_id === userPreference.frontline,
    );
    if (!frontlineItem) return;
    // A saved frontline can become inactive later; the menu disables it, so it
    // must not be programmatically restored either.
    if (isOptionDisabled(frontlineItem.status)) return;
    prefFrontlineAppliedRef.current = true;
    setSelectedFrontlineId(frontlineItem.frontline_id);
    setFrontlineId(frontlineItem.frontline_id);
    setSelectedFronlineName(frontlineItem.frontline_code);
  }, [frontline, userPreference?.frontline]);

  // 4. Country (stored as one or more country ids) -> map to country_codes
  React.useEffect(() => {
    if (prefCountryAppliedRef.current) return;
    if (!userPreference?.country) return;
    if (!countryList || countryList.length === 0) return;
    // Backward compatible with a single-id value from older saved preferences.
    const preferredCountryIds = Array.isArray(userPreference.country)
      ? userPreference.country
      : [userPreference.country];
    if (preferredCountryIds.length === 0) return;
    // Skip any saved country that has since become inactive; the menu disables
    // those options, so they must not be programmatically restored either.
    const countryItems = countryList.filter(
      (c) =>
        preferredCountryIds.includes(c.country_id) &&
        !isOptionDisabled(c.status),
    );
    if (countryItems.length === 0) return;
    prefCountryAppliedRef.current = true;
    const selectedCodes = countryItems.map((c) => c.country_code);
    setSelectedCountryCode(selectedCodes);
    setSelectedCountryName(countryItems.map((c) => c.name).join(", "));
    setCountry(selectedCodes);
    setFormattedCountryCodes(
      selectedCodes.map((code) => `"${code}"`).join(","),
    );
  }, [countryList, userPreference?.country]);

  // 5. Supplier type (no dependency)
  React.useEffect(() => {
    if (prefSupplierAppliedRef.current) return;
    if (!userPreference?.supplierType) return;
    prefSupplierAppliedRef.current = true;
    setSupplierType(userPreference.supplierType);
    if (userPreference.supplierType === "supplier") {
      setSelectedSupplier(
        t("adminDashboard.dropdownSelection.supplierTypes.koneInternal"),
      );
    } else if (userPreference.supplierType === "vendor") {
      setSelectedSupplier(
        t("adminDashboard.dropdownSelection.supplierTypes.subcontractor"),
      );
    } else {
      setSelectedSupplier(userPreference.supplierType);
    }
  }, [userPreference?.supplierType]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    type: "region" | "subRegion" | "country" | "businessType",
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const fetchRegions = async () => {
    const loadingToken = beginLoading("breadcrumb-regions");
    try {
      const token = await getToken();

      // Fetch region data
      const regionData = await fetchRegionData(
        token,
        userPreference.languagePreference,
      );
      setRegions(regionData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const handleMenuSelect = (
    type:
      | "region"
      | "subRegion"
      | "country"
      | "supplier"
      | "vendor"
      | "businessType",
    value,
    name,
  ) => {
    if (type) {
      if (type === supplierType && value === selectedSupplier) {
        setAnchorEl(null); // Close the menu if the same supplier or vendor is selected
        return; // Prevent resetting if the same supplier or vendor is selected
      }
      resetCompanyList(); // Reset company list when a new region is selected
    }
    if (type === "businessType") {
      handleBusinessTypeSelect(value);
    } else if (type === "region") {
      // Reset sub-region, country, and supplier when region is selected
      setSelectedFrontlineId("");
      setSelectedFronlineName(t("adminDashboard.dropdownSelection.frontline"));
      setSelectedCountryCode([]);
      setSelectedCountryName("");
      setSelectedSupplier("");
      setFrontline([]);
      setCountries([]);
      setVendors([]);
      setCountryList([]);

      // Reset global state values
      setFrontlineId(""); // Reset frontlineId in the context
      setCountry(""); // Reset country in the context
      setFormattedCountryCodes([]); // Reset formattedCountryCodes in the context
      setSupplierType(""); // Reset supplierType in the context

      // Set selected region
      setSelectedRegionId(value);
      setRegionId(value);
      setSelectedRegionName(name);
      setFrontline(""); // Reset frontLine in the context
      setCountry(""); // Reset country in the context

      // Persist to global preference; dependent fields are reset alongside it.
      setUserPreference((prev) => ({
        ...prev,
        area: value,
        frontline: null,
        country: null,
        supplierType: null,
      }));

      // Fetch new sub-regions based on selected region
      fetchSubRegionsForRegion(value);
    } else if (type === "subRegion") {
      // Reset global state values
      setCountry(""); // Reset country in the context
      setFormattedCountryCodes([]); // Reset formattedCountryCodes in the context
      setFrontlineId(value);
      setSelectedFrontlineId(value);
      setSelectedFronlineName(name);
      setSelectedCountryCode([]);
      setSelectedCountryName("");
      setCountries([]);
      setVendors([]);

      // Persist to global preference; country is reset alongside it.
      setUserPreference((prev) => ({
        ...prev,
        frontline: value,
        country: null,
      }));

      fetchCountryDataForSubRegion(value); // Re-fetch country data for new sub-region
    } else if (type === "country") {
      setCountry(value);
      setSelectedCountryCode(value);
      setSelectedCountryName(name);
      setFormattedCountryCodes(value.map((code) => `"${code}"`).join(","));

      // Preference stores all selected country ids (multi-select).
      const selectedCountryIds = countryList
        .filter((c) => value.includes(c.country_code))
        .map((c) => c.country_id);
      setUserPreference((prev) => ({ ...prev, country: selectedCountryIds }));
    } else if (type === "supplier") {
      setSupplierType("supplier");
      setSelectedSupplier(value);
      setUserPreference((prev) => ({ ...prev, supplierType: "supplier" }));
    } else if (type === "vendor") {
      setSupplierType("vendor");
      setSelectedSupplier(value);
      setUserPreference((prev) => ({ ...prev, supplierType: "vendor" }));
    }

    setAnchorEl(null);
  };

  const fetchSubRegionsForRegion = async (regionId: number) => {
    const loadingToken = beginLoading("breadcrumb-sub-regions");
    try {
      const token = await getToken();
      const frontlineData = await fetchFrontlineData(token, regionId);
      setFrontline(frontlineData);
    } catch (error) {
      console.error("Error fetching sub-region data:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const fetchCountryDataForSubRegion = async (subRegionId: string) => {
    const loadingToken = beginLoading("breadcrumb-sub-region-countries");
    try {
      const token = await getToken();
      const countryData = await fetchCountryData(
        token,
        subRegionId,
        userPreference.languagePreference,
      );
      setCountries(countryData);
    } catch (error) {
      console.error("Error fetching country data:", error);
    } finally {
      endLoading(loadingToken);
    }
  };

  const isOptionDisabled = (status: string) => {
    // Disable the option if it's the status is inactive
    return status === "inactive";
  };

  // Get display name for selected business type
  const getBusinessTypeDisplayName = () => {
    if (!selectedBusinessType) {
      return t(
        "adminDashboard.dropdownSelection.businessType",
        "Business Type",
      );
    }
    const found = businessTypesList?.find(
      (bt) => bt.businessTypeName === selectedBusinessType,
    );
    return found
      ? t(`businessTypes.${found.businessTypeName}`, found.businessTypeName)
      : selectedBusinessType;
  };

  // Handle business type selection
  const handleBusinessTypeSelect = async (businessType: any) => {
    await fetchRegions(); // Fetch regions for the new business type

    setSelectedBusinessType(businessType.businessTypeName);
    setSelectedBusinessTypeId(businessType.businessTypeId);
    setBusinessTypeAnchorEl(null);

    // Persist to global preference; dependent fields are reset alongside it.
    setUserPreference((prev) => ({
      ...prev,
      businessType: businessType.businessTypeId,
      area: null,
      frontline: null,
      country: null,
      supplierType: null,
    }));

    // Reset all dependent selections when business type changes
    resetCompanyList();
    setSelectedRegionId(null);
    setSelectedRegionName(t("adminDashboard.dropdownSelection.region"));
    setSelectedFrontlineId("");
    setSelectedFronlineName(t("adminDashboard.dropdownSelection.frontline"));
    setSelectedCountryCode([]);
    setSelectedCountryName("");
    setSelectedSupplier("");
    setFrontline([]);
    setCountryList([]);
    setRegionId("");
    setFrontlineId("");
    setCountry("");
    setFormattedCountryCodes([]);
    setSupplierType("");

    // Reset training/qualification data when business type changes
    setTrainingHeaders([]);
    // Reset breadcrumbs to initial state (keep root "Qualifications" item)
    setBreadcrumbs([
      {
        key: "",
        value: 0,
        label: t("adminDashboard.breadcrumb.qualification"),
      },
    ]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#F3F6FE 0% 0% no-repeat padding-box",
        height: "46px",
        paddingRight: "16px",
      }}
    >
      <Breadcrumbs
        separator={
          <img
            src="/icon_breadcrumb_chevron.svg"
            alt="Chevron"
            style={{ width: "13px", height: "40px", opacity: 1 }}
          />
        }
        aria-label="breadcrumb"
        style={{
          top: "86px",
          left: "0px",
          // width: '1281px',
          height: "46px",
          background: "#F3F6FE 0% 0% no-repeat padding-box",
          opacity: "1",
        }}
      >
        {/* Business Type Button - First dropdown */}
        <div
          title={t(
            "adminDashboard.dropdownSelection.businessType",
            "Business Type",
          )}
        >
          <Button
            onClick={(e) => handleMenuOpen(e, "businessType")}
            style={{
              color: "black",
              fontSize: "14px",
              textTransform: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              fontFamily: "Inter",
            }}
          >
            {getBusinessTypeDisplayName()}
            <KeyboardArrowDown
              style={{ fontSize: "20px", marginLeft: "4px" }}
            />
          </Button>
        </div>

        {/* Region Button */}
        <div title={t("adminDashboard.dropdownSelection.region")}>
          <Button
            onClick={(e) => handleMenuOpen(e, "region")}
            style={{
              color: "black",
              fontSize: "14px",
              textTransform: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              fontFamily: "Inter",
            }}
          >
            {selectedRegionName || t("adminDashboard.dropdownSelection.region")}
            <KeyboardArrowDown
              style={{ fontSize: "20px", marginLeft: "4px" }}
            />
          </Button>
        </div>

        {/* Sub-Region Button */}
        <div title={t("adminDashboard.dropdownSelection.frontline")}>
          <Button
            onClick={(e) => handleMenuOpen(e, "subRegion")}
            style={{
              color: "black",
              fontSize: "14px",
              textTransform: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              fontFamily: "Inter",
            }}
          >
            {selectedFrontlineName ||
              t("adminDashboard.dropdownSelection.frontline")}
            <KeyboardArrowDown
              style={{ fontSize: "20px", marginLeft: "4px" }}
            />
          </Button>
        </div>

        {/* Country Button */}
        <div title={t("adminDashboard.dropdownSelection.country")}>
          <Button
            onClick={(e) => handleMenuOpen(e, "country")}
            style={{
              color: "black",
              fontSize: "14px",
              textTransform: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              fontFamily: "Inter",
            }}
          >
            {selectedCountryName ||
              t("adminDashboard.dropdownSelection.country")}
            <KeyboardArrowDown
              style={{ fontSize: "20px", marginLeft: "4px" }}
            />
          </Button>
        </div>

        {/* Supplier Button */}
        <div title={t("adminDashboard.dropdownSelection.supplier")}>
          <Button
            onClick={(e) => handleMenuOpen(e, "supplier")}
            style={{
              color: "black",
              fontSize: "14px",
              textTransform: "none",
              padding: "0",
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              fontFamily: "Inter",
            }}
          >
            {selectedSupplier || t("adminDashboard.dropdownSelection.supplier")}
            <KeyboardArrowDown
              style={{ fontSize: "20px", marginLeft: "4px" }}
            />
          </Button>
        </div>
      </Breadcrumbs>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {/* Render Business Type options dynamically */}
        {menuType === "businessType" &&
          businessTypesList?.map((bt) => (
            <MenuItem
              key={bt.businessTypeId}
              onClick={() =>
                handleMenuSelect("businessType", bt, bt.businessTypeName)
              }
              selected={selectedBusinessType === bt.businessTypeName}
            >
              <TextLabel type="regularText" variant="body2">
                {t(`businessTypes.${bt.businessTypeName}`, bt.businessTypeName)}
              </TextLabel>
            </MenuItem>
          ))}
        {/* Render Region options dynamically */}
        {menuType === "region" &&
          regions?.map((region, index) => (
            <MenuItem
              key={index}
              onClick={() =>
                handleMenuSelect("region", region.region_id, region.region_code)
              }
              disabled={isOptionDisabled(region.status)} // Disable option if status is inactive
            >
              <TextLabel type="regularText" variant="body2">
                {region?.region_code}
              </TextLabel>
            </MenuItem>
          ))}

        {/* Render Sub-Region options dynamically */}
        {menuType === "subRegion" &&
          frontline?.map((subRegion, index) => (
            <MenuItem
              key={index}
              onClick={() =>
                handleMenuSelect(
                  "subRegion",
                  subRegion.frontline_id,
                  subRegion.frontline_code,
                )
              }
              disabled={isOptionDisabled(subRegion.status)} // Disable option if status is inactive
            >
              <TextLabel type="regularText" variant="body2">
                {subRegion.frontline_code}
              </TextLabel>
            </MenuItem>
          ))}

        {/* Render Country options dynamically */}

        {menuType === "country" &&
          countryList.map((country, index) => {
            const isSelected = selectedCountryCode.includes(
              country.country_code,
            );

            return (
              <MenuItem
                key={index}
                onClick={() => {
                  const updatedSelectedCountries = isSelected
                    ? selectedCountryCode.filter(
                        (code) => code !== country.country_code,
                      )
                    : [...selectedCountryCode, country.country_code];

                  setSelectedCountryCode(updatedSelectedCountries);

                  const updatedSelectedCountryNames = isSelected
                    ? selectedCountryName
                        .split(", ")
                        .filter((name) => name !== country.name)
                        .join(", ")
                    : !selectedCountryName ||
                        selectedCountryName === "Select Country"
                      ? country.name
                      : `${selectedCountryName}, ${country.name}`;

                  setSelectedCountryName(updatedSelectedCountryNames);

                  handleMenuSelect(
                    "country",
                    updatedSelectedCountries,
                    updatedSelectedCountryNames,
                  );
                }}
                selected={isSelected}
                disabled={isOptionDisabled(country.status)} // Disable option if status is inactive
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <TextLabel type="regularText" variant="body2">
                    {country.name}
                  </TextLabel>
                  {isSelected && (
                    <span
                      style={{
                        marginLeft: "8px",
                        color: "red",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onClick
                        const updatedSelectedCountries =
                          selectedCountryCode.filter(
                            (code) => code !== country.country_code,
                          );
                        setSelectedCountryCode(updatedSelectedCountries);

                        const updatedSelectedCountryNames = selectedCountryName
                          .split(", ")
                          .filter((name) => name !== country.name)
                          .join(", ");
                        setSelectedCountryName(updatedSelectedCountryNames);

                        handleMenuSelect(
                          "country",
                          updatedSelectedCountries,
                          updatedSelectedCountryNames,
                        );
                      }}
                    >
                      ✕
                    </span>
                  )}
                </div>
              </MenuItem>
            );
          })}

        {/* Add Kone Internal and Subcontractor options */}
        {menuType === "supplier" && (
          <>
            {!isSubconAdmin && (
            <MenuItem
              onClick={() =>
                handleMenuSelect(
                  "supplier",
                  t(
                    "adminDashboard.dropdownSelection.supplierTypes.koneInternal",
                  ),
                  "Kone Internal",
                )
              }
            >
              <TextLabel type="regularText" variant="body2">
                {t(
                  "adminDashboard.dropdownSelection.supplierTypes.koneInternal",
                )}
              </TextLabel>
            </MenuItem>
            )}
            <MenuItem
              onClick={() =>
                handleMenuSelect(
                  "vendor",
                  t(
                    "adminDashboard.dropdownSelection.supplierTypes.subcontractor",
                  ),
                  "Subcontractor",
                )
              }
            >
              <TextLabel type="regularText" variant="body2">
                {t(
                  "adminDashboard.dropdownSelection.supplierTypes.subcontractor",
                )}
              </TextLabel>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default BreadcrumbNavigation;
